import { useEffect, useReducer } from "react";
import "../../../app/App.scss";
import { IntlProvider, useIntl } from "react-intl";
import ENGLISH from "../../../i18n/languages/en-US.json";
import { useParams } from "react-router-dom";
import { pig } from "../../../static/js/pig.js";
import Header from "./Header";
import Loader from "../../common/elements/Loader";
import translate from "@/i18n/translate";
import { sendRequest } from "@/common/http";
import {
  WmProgressIndicator,
  WmProgressMonitor,
  WmProgressSlice
} from "@watermarkinsights/ripple-react";
import { formatDateFullYear } from "@/features/common/helper";
import { DrilldownHierarchy, arrow } from "../../ces/ResponseRate";
import { RenderHierarchyTable } from "@/features/ces/OpenDrillDownViewResponseRate";
import UnauthorizedComponent from "@/features/insights/UnauthorizedComponent";
import ApiError from "@/features/common/elements/ApiError";

interface HtmlState {
  locale: string;
  messages: object | null;
  responseRate: ResponseRateState;
  drilldownHierarchy: DrilldownHierarchy | null;
  responseRateError: boolean;
  nodeResponseRateError: boolean;
}
interface ResponseRateState {
  average: number;
  averageDifference: number;
  awaiting: string;
  isAverageDifferencePositive: boolean;
  responded: string;
  previousProject: PreviousProject;
  selectedProject: CourseEvaluation;
}

interface PreviousProject {
  diffInResponseRate: number;
  isDiffPositive: boolean;
  responseRate: number;
  title: string;
}

interface CourseEvaluation {
  endDate: string;
  projectStatus: string;
  startDate: string;
  title: string;
  projectId: number | null;
  surveyId: number | null;
  daysRemaining: number;
}

interface Action {
  type: string;
  locale: string;
  responseRate: ResponseRateState;
  drilldownHierarchy: DrilldownHierarchy | null;
  responseRateError: boolean;
  nodeResponseRateError: boolean;
}

interface UrlParams {
  survey_id: string;
  project_id: string;
}
const defaultCourseEvaluation: CourseEvaluation = {
  endDate: "",
  projectStatus: "",
  startDate: "",
  title: "",
  projectId: null,
  surveyId: null,
  daysRemaining: 0
};
const defaultPreviousProject: PreviousProject = {
  diffInResponseRate: 0,
  isDiffPositive: false,
  responseRate: 0,
  title: ""
};

const defaultResponseRateState: ResponseRateState = {
  average: 0,
  averageDifference: 0,
  awaiting: "",
  isAverageDifferencePositive: false,
  responded: "",
  previousProject: defaultPreviousProject,
  selectedProject: defaultCourseEvaluation
};

const defaultAction: Action = {
  type: "",
  locale: "en-US",
  responseRate: defaultResponseRateState,
  drilldownHierarchy: null,
  responseRateError: false,
  nodeResponseRateError: false
};
const reducer = (state: HtmlState, action: Action) => {
  switch (action.type) {
    case "setLocale":
      return setLocale(state, action.locale);
    case "setResponseRate":
      return { ...state, responseRate: action.responseRate };
    case "setResponseRateError":
      return { ...state, responseRateError: action.responseRateError };
    case "setHierarchy":
      return {
        ...state,
        drilldownHierarchy: action.drilldownHierarchy
      };
    case "setNodeResponseRateError":
      return { ...state, nodeResponseRateError: true };
    default:
      return state;
  }
};

const setLocale = (state: HtmlState, locale: string | null) => {
  switch (locale) {
    case "pig": {
      return { ...state, locale: "pig", messages: pig(ENGLISH) };
    }
    default: {
      return { ...state, locale: "en-US", messages: ENGLISH };
    }
  }
};

const initialState: HtmlState = {
  locale: "en-US",
  messages: null,
  responseRate: defaultResponseRateState,
  drilldownHierarchy: null,
  responseRateError: false,
  nodeResponseRateError: false
};

export interface HtmlViewProps {
  auth: Boolean;
}

export default function HtmlView({ auth }: HtmlViewProps): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { survey_id: surveyId, project_id: projectId } = useParams<UrlParams>();

  useEffect(() => {
    const queryString = window.location.search;
    const param = new URLSearchParams(queryString);
    const locale = param.get("locale") || initialState.locale;
    dispatch({ ...defaultAction, type: "setLocale", locale: locale });
  }, []);

  useEffect(() => {
    if (surveyId && projectId) {
      const applyResult = (result: ResponseRateState) => {
        dispatch({
          ...defaultAction,
          type: "setResponseRate",
          responseRate: result
        });
      };
      const onError = () => {
        dispatch({
          ...defaultAction,
          type: "setResponseRateError",
          responseRateError: true
        });
      };
      auth &&
        sendRequest(
          {
            uri: `/ces/response_rate/survey/${surveyId}/project/${projectId}`,
            camelize: true
          },
          applyResult,
          onError
        );
    }
  }, [auth, surveyId, projectId]);

  useEffect(() => {
    if (surveyId && projectId) {
      const applyResult = (result: DrilldownHierarchy) => {
        dispatch({
          ...defaultAction,
          type: "setHierarchy",
          drilldownHierarchy: result
        });
      };
      const onError = () => {
        dispatch({
          ...defaultAction,
          type: "setNodeResponseRateError"
        });
      };
      auth &&
        sendRequest(
          {
            uri: `/ces/node_response_rate/project/${projectId}`,
            camelize: true
          },
          applyResult,
          onError
        );
    }
  }, [auth, surveyId, projectId]);

  const showLoader =
    state.responseRate.selectedProject.projectId === null &&
    state.drilldownHierarchy === null &&
    !state.responseRateError &&
    !state.nodeResponseRateError;
  return (
    <>
      {auth ? (
        <div className="pagewrapper ces-html-container">
          <div className="insights-hub-body flex-row">
            <div className="nav-main">
              <div className="contentwrapper">
                <main id="maincontent" className="main-container">
                  {state.messages && (
                    <IntlProvider
                      locale={state.locale}
                      messages={state.messages}
                    >
                      <div id="cesHtml" className="ces-html-container">
                        {showLoader && <Loader />}
                        <Header
                          showDownloadBtn={
                            !state.responseRate || !state.nodeResponseRateError
                          }
                        />
                        <section
                          className="-mainheader -withbreadcrumbs only-breadcrumbs body-padding"
                          id="page-header"
                          data-export-id="ces-export"
                        >
                          {!state.responseRateError && (
                            <ProjectDetails
                              project={state.responseRate.selectedProject}
                            />
                          )}

                          <div
                            id="response-rate-html"
                            className="response-rate-container"
                          >
                            {state.responseRateError ? (
                              <ApiError containerClasses="api-error" />
                            ) : (
                              <>
                                <ResponseRateChart
                                  responseRate={state.responseRate}
                                />
                                <ResponseRateTrends
                                  responseRate={state.responseRate}
                                />
                              </>
                            )}
                            <ResponseRateTable
                              drilldownHierarchy={state.drilldownHierarchy}
                              apiError={state.nodeResponseRateError}
                            />
                          </div>
                        </section>
                      </div>
                    </IntlProvider>
                  )}
                </main>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <UnauthorizedComponent />
      )}
    </>
  );
}

interface ProjectDetailsProps {
  project: CourseEvaluation;
}
const ProjectDetails = ({ project }: ProjectDetailsProps) => {
  return (
    <>
      <div className="flex-row -justified plan-status-container plan-status-container-html">
        <div className="plan-title">{project.title}</div>
        <CourseEvaluationStatus status={project.projectStatus} />
      </div>
      <div className="ces-duration">
        <div className="ces-details">
          {translate("app.ces.responseRate.details")}
        </div>
        <div className="ces-dates">
          <span className="mdi mdi-calendar-clock"></span>
          {`${formatDateFullYear(project.startDate)} - ${formatDateFullYear(
            project.endDate
          )}`}
        </div>
        {project.daysRemaining > 0 && (
          <div className="ces-days">
            <span className="mdi mdi-timer-sand"></span>

            {translate("app.ces.responseRate.daysRemaining", {
              daysRemaining: project.daysRemaining
            })}
          </div>
        )}
      </div>
    </>
  );
};
interface CourseEvaluationStatusProps {
  status: string;
}
const CourseEvaluationStatus = ({ status }: CourseEvaluationStatusProps) => {
  switch (status) {
    case "InProgress":
      return (
        <div className="in-progress-status">
          <div className="title-text">
            {translate("app.drilldown.inProgress")}
          </div>
        </div>
      );
    case "Ended":
      return (
        <div className="ended-status -completed">
          <div className="title-text">{translate("app.drilldown.ended")}</div>
        </div>
      );
    default:
      return <></>;
  }
};
interface ResponseRateChartProps {
  responseRate: ResponseRateState;
}
const ResponseRateChart = ({ responseRate }: ResponseRateChartProps) => {
  const project = responseRate.selectedProject;

  const respondedText = useIntl().formatMessage({
    id: "app.ces.responseRate.responded"
  });
  const popoverRespondedText = useIntl().formatMessage({
    id: "app.ces.responseRate.respondedPopover"
  });
  const popoverAwaitingText = useIntl().formatMessage({
    id: "app.ces.responseRate.respondedAwaitingPopover"
  });

  const awaitingText = useIntl().formatMessage({
    id: "app.ces.responseRate.awaiting"
  });

  const respondedAwaiting = useIntl().formatMessage({
    id: "app.ces.responseRate.respondedAwaiting"
  });
  const responseRateLabel = useIntl().formatMessage({
    id: "app.ces.responseRate.responseRate"
  });
  return (
    <>
      <div className="response-rate-label">
        {translate("app.ces.responseRate.overallResponseRate")}
      </div>
      <div className="response-rate-chart">
        <WmProgressMonitor
          id="ces-response-rate"
          group-legend={respondedAwaiting}
          breakpoint="10px"
        >
          <WmProgressIndicator label={responseRateLabel}>
            <WmProgressSlice
              amount={responseRate.responded}
              popover-text={popoverRespondedText}
              legend={respondedText}
              popover-title={`${responseRate.responded}%`}
              title={project.title}
            />
            <WmProgressSlice
              amount={responseRate.awaiting}
              popover-text={popoverAwaitingText}
              legend={awaitingText}
              popover-title={`${responseRate.awaiting}%`}
              title={project.title}
            />
          </WmProgressIndicator>
        </WmProgressMonitor>
      </div>
    </>
  );
};
interface ResponseRateTrendsProps {
  responseRate: ResponseRateState;
}
const ResponseRateTrends = ({ responseRate }: ResponseRateTrendsProps) => {
  return (
    <div className="response-rate-divider">
      <div className="response-rate-trends">
        <div className="response-rate-trends-heading">
          {translate("app.ces.responseRate.responseRateTrends")}
        </div>
        {responseRate.average !== null ? (
          <div className="response-rate-trends-container">
            <div className="response-rate-all-courses">
              <div className="flex-row">
                <span
                  className={`-icons mdi ${arrow(
                    responseRate.isAverageDifferencePositive
                  )}`}
                ></span>
                <div className="comparison-rate-all-courses">
                  {responseRate.averageDifference}%
                </div>
              </div>
              <div className="response-rate-trends-desc">
                {translate(
                  "app.ces.responseRate.responseRateTrendsDescriptionAllCourses",
                  {
                    comparisonRate: responseRate.average
                  }
                )}
              </div>
            </div>
            <div className="response-rate-previous-course">
              {responseRate.previousProject && (
                <>
                  <div className="flex-row">
                    <span
                      className={`-icons mdi ${arrow(
                        responseRate.previousProject.isDiffPositive
                      )}`}
                    ></span>
                    <div className="comparison-rate-all-courses">
                      {responseRate.previousProject.diffInResponseRate}%
                    </div>
                  </div>
                  <div className="response-rate-trends-desc">
                    {translate(
                      "app.ces.responseRate.responseRateTrendsDescriptionPreviousCourse",
                      {
                        previousProjectTitle:
                          responseRate.previousProject.title,
                        comparisonRate:
                          responseRate.previousProject.responseRate
                      }
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <EmptyResponseRateTrends />
        )}
      </div>
    </div>
  );
};

const EmptyResponseRateTrends = () => {
  return (
    <div className="flex-row no-response-rate">
      <span className="-icons mdi mdi-timer-sand"></span>
      <div className="no-response-rate-text">
        {translate("app.ces.responseRate.NoResponseRateText")}
      </div>
    </div>
  );
};

interface ResponseRateTableProps {
  drilldownHierarchy: DrilldownHierarchy | null;
  apiError: boolean;
}

const ResponseRateTable = ({
  drilldownHierarchy,
  apiError
}: ResponseRateTableProps): JSX.Element => {
  return (
    <>
      <h2 className="ces-duration ces-duration-drilldown">
        {translate("app.ces.responseRate.byOrganzationProgram")}
      </h2>
      {apiError ? (
        <ApiError containerClasses="api-error" />
      ) : drilldownHierarchy ? (
        <div className="table-border">
          <RenderHierarchyTable completeHierarchy={drilldownHierarchy} />
        </div>
      ) : (
        <div className="loader">
          <div className="content content-loader"></div>
        </div>
      )}
    </>
  );
};
